import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, CalendarClock, Send, Trash2, RefreshCw, Copy, Pencil, Image as ImageIcon, Images, Filter } from 'lucide-react';
import api from '../services/apiClient';
import useAuthStore from '../store/authStore';
import { Button, Input, StatusBadge } from '../components/ui';
import { toast } from '../components/toast';
import Lightbox from '../components/Lightbox';
import { NETWORK_ICONS, NETWORK_COLORS } from '../lib/networks';

const FILTERS = [
  { key: '', label: 'Todos' },
  { key: 'draft', label: 'Rascunhos' },
  { key: 'approved', label: 'Aprovados' },
  { key: 'scheduled', label: 'Agendados' },
  { key: 'published', label: 'Publicados' }
];

export default function Calendar() {
  const currentCompany = useAuthStore((s) => s.currentCompany);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [scheduleFor, setScheduleFor] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const companyId = currentCompany?.id;

  const load = async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter) params.status = filter;
      if (from) params.from = `${from}T00:00:00`;
      if (to) params.to = `${to}T23:59:59`;
      const data = await api.get('/posts', params);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, filter, from, to]);

  // Atalho: define o intervalo a partir de hoje por N dias
  const presetDays = (days) => {
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const now = new Date();
    setFrom(fmt(now));
    setTo(fmt(new Date(now.getTime() + days * 864e5)));
  };

  const clearDates = () => { setFrom(''); setTo(''); };

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (err) { toast.error(err.message); }
  };

  const approve = (id) => act(() => api.post(`/posts/${id}/approve`), 'Aprovado');
  const publishNow = (id) => act(() => api.post(`/posts/${id}/publish-now`), 'Enviado ao Metricool');
  const remove = (id) => { if (confirm('Excluir este post?')) act(() => api.del(`/posts/${id}`), 'Excluído'); };
  const duplicate = (id) => act(() => api.post(`/posts/${id}/duplicate`), 'Post reutilizado como rascunho');
  const schedule = (id) => {
    const dt = scheduleFor[id];
    if (!dt) return toast.error('Escolha data e hora');
    act(() => api.post(`/posts/${id}/schedule`, { scheduledAt: new Date(dt).toISOString() }), 'Agendado');
  };

  if (!companyId) return <p className="muted">Selecione uma empresa no topo.</p>;

  return (
    <div>
      <div className="row between wrap mb8" style={{ gap: 12 }}>
        <div className="cal-tabs">
          {FILTERS.map((f) => (
            <button key={f.key} className={`cal-tab ${filter === f.key ? 'on' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <Button variant="secondary" onClick={load}><RefreshCw size={15} /> Atualizar</Button>
      </div>

      <div className="cal-filters mb16">
        <span className="muted" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Filter size={14} /> Período:
        </span>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="De" />
        <span className="muted">até</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Até" />
        <span className="net-chip" onClick={() => presetDays(7)}>Próx. 7 dias</span>
        <span className="net-chip" onClick={() => presetDays(30)}>Próx. 30 dias</span>
        {(from || to) && <button className="cal-clear" onClick={clearDates}>Limpar</button>}
      </div>

      {loading ? <p className="muted">Carregando…</p> : (
        <div className="cal-list">
          {items.length === 0 && (
            <div className="card center muted" style={{ padding: 40 }}>
              Nenhum post {filter ? 'neste filtro' : ''}. Gere conteúdo na aba "Gerar conteúdo".
            </div>
          )}
          {items.map((post) => {
            const nets = (post.targets || []).map((t) => t.network);
            return (
              <div key={post.id} className="cal-card">
                <div className="cal-cover" onClick={() => (post.media && post.media.length) ? setLightbox(post.media.map((m) => m.url)) : navigate(`/posts/${post.id}`)}>
                  {post.coverUrl
                    ? <img src={post.coverUrl} alt="" />
                    : <span className="cal-cover-empty"><ImageIcon size={22} /></span>}
                  {post.mediaCount > 1 && <span className="cal-count"><Images size={11} /> {post.mediaCount}</span>}
                </div>

                <div className="cal-main">
                  <div className="cal-top">
                    <StatusBadge status={post.status} />
                    <span className="cal-nets">
                      {nets.map((n, i) => {
                        const Icon = NETWORK_ICONS[n];
                        return Icon ? <Icon key={i} size={15} color={NETWORK_COLORS[n]} /> : null;
                      })}
                    </span>
                    <span className="spacer" />
                    <span className="cal-when">
                      {post.scheduledAt
                        ? <><CalendarClock size={13} /> {new Date(post.scheduledAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</>
                        : <span className="muted">sem data</span>}
                    </span>
                  </div>

                  <strong className="cal-title" onClick={() => navigate(`/posts/${post.id}`)}>{post.title || 'Sem título'}</strong>
                  {post.preview && <p className="cal-preview">{post.preview}</p>}
                  {post.publishError && <p className="cal-err">Falha: {post.publishError}</p>}

                  <div className="cal-actions">
                    <Button className="sm" onClick={() => navigate(`/posts/${post.id}`)}><Pencil size={14} /> Abrir</Button>
                    {post.status === 'draft' && (
                      <Button variant="secondary" className="sm" onClick={() => approve(post.id)}><CheckCircle2 size={14} /> Aprovar</Button>
                    )}
                    {post.status !== 'published' && (
                      <span className="cal-sched">
                        <Input type="datetime-local" value={scheduleFor[post.id] || ''}
                          onChange={(e) => setScheduleFor((s) => ({ ...s, [post.id]: e.target.value }))} />
                        <Button className="sm" onClick={() => schedule(post.id)}><CalendarClock size={14} /> Agendar</Button>
                      </span>
                    )}
                    <span className="spacer" />
                    {post.status !== 'published' && (
                      <Button variant="ghost" className="sm" onClick={() => publishNow(post.id)} title="Publicar agora"><Send size={15} /></Button>
                    )}
                    <Button variant="ghost" className="sm" onClick={() => duplicate(post.id)} title="Reutilizar"><Copy size={15} /></Button>
                    <Button variant="ghost" className="sm" onClick={() => remove(post.id)} title="Excluir"><Trash2 size={15} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Lightbox images={lightbox || []} onClose={() => setLightbox(null)} />
    </div>
  );
}
